import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import * as THREE from 'three';

// Custom Hooks
import useThreeEngine from './useThreeEngine';
import useMeshUpdater from './useMeshUpdater';
import useIndicators from './useIndicators';

// Constants & Utilities
import {
  theme,
  slotPositions,
  connectorOptions,
  housingOptionsFull,
  housingOptionsRestricted,
  LAMBDA_URL,
  ADMIN_EMAILS
} from './constants';

// UI Components
import './App.css';
import Sidebar from './Sidebar';
import OptionsSidebar from './OptionsSidebar';
import PrintableArea from './PrintableArea';
import AdminModal from './AdminModal';
import ContactModal from './ContactModal';
import IODesigner from './IODesigner';

/* eslint-disable */
if (typeof window !== 'undefined') {
  // Fixes the infinite loop error on React Flow library
  const RO = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends RO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };

  // Console silence
  const originalError = console.error;
  console.error = (...args) => {
    if (args[0]?.includes?.('ResizeObserver loop')) return;
    originalError.apply(console, args);
  };

  // Early interception
  window.addEventListener('error', (e) => {
    if (e.message?.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
}

const WarningModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '400px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '18px', fontWeight: 900, color: theme.primary, marginBottom: '15px' }}>RESTRICTION</div>
        <p style={{ fontSize: '14px', color: '#334155', lineHeight: '1.6', marginBottom: '25px', fontWeight: 500 }}>
          If <strong>Q5</strong> is empty, then <strong>Q6-Q8</strong> must also be empty.
          <br /><br />
          If you do not want to use Q5, select a <strong>PLUG</strong> on it.
        </p>
        <button onClick={onClose} style={{ background: theme.primary, color: 'white', border: 'none', padding: '12px 30px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}>
          OK
        </button>
      </div>
    </div>
  );
};

const componentOptions = {
  Q1: { label: 'Bottom Connector', options: connectorOptions },
  Q2: { label: 'Top Connector', options: connectorOptions },
  Q3: { label: 'Bottom Connector', options: connectorOptions },
  Q4: { label: 'Top Connector', options: connectorOptions },
  Q5: { label: 'Front Housing', options: housingOptionsFull },
  Q6: { label: 'Front Housing', options: housingOptionsRestricted },
  Q7: { label: 'Front Housing', options: housingOptionsRestricted },
  Q8: { label: 'Front Housing', options: housingOptionsRestricted },
  Q9: {
    label: 'Top Housing Style', options: [
      { variant: 'top', name: 'LED Display', code: 'LD' },
      { variant: 'basic_top', name: 'No LED Display', code: '00' }
    ]
  },
  inner_components: {
    label: 'Internal Hardware', options: [
      { variant: 'sd_card', name: 'SD Card' },
      { variant: 'relay', name: 'Relay' },
      { variant: 'can_double', name: 'CAN Interface' },
      { variant: 'antenna', name: 'Connector for External Antenna' },
      { variant: 'back_holes', name: 'Back Holes' }
    ]
  }
};

const initialState = {
  Q1: connectorOptions.find(o => o.variant === 'none'),
  Q2: connectorOptions.find(o => o.variant === 'none'),
  Q3: connectorOptions.find(o => o.variant === 'none'),
  Q4: connectorOptions.find(o => o.variant === 'none'),
  Q5: housingOptionsFull.find(o => o.variant === 'e_stop_led'),
  Q6: housingOptionsRestricted.find(o => o.variant === 'none'),
  Q7: housingOptionsRestricted.find(o => o.variant === 'none'),
  Q8: housingOptionsRestricted.find(o => o.variant === 'none'),
  Q9: { variant: 'top', name: 'LED Display', code: 'LD' },
  inner_components: []
};

const Configurator = () => {
  // Auth and modal states
  const { instance, accounts } = useMsal();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIODesignerOpen, setIsIODesignerOpen] = useState(false);
  const [ioNodes, setIoNodes] = useState([]);
  const [adminPrices, setAdminPrices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [changedRows, setChangedRows] = useState(new Set());
  const [deletedCodes, setDeletedCodes] = useState(new Set());
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [buttonColors, setButtonColors] = useState({});
  const isAuthenticated = accounts.length > 0;
  const isAdmin = accounts.length > 0 &&
    ADMIN_EMAILS.includes(accounts[0].username.toLowerCase());

  const fetchAdminPrices = useCallback(async () => {
    // Safety check
    if (accounts.length === 0) return;

    try {
      // Acquire token from Entra ID
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      const token = tokenResponse.accessToken;

      // Pass token in the Authorization header
      const res = await fetch(LAMBDA_URL, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error(`Server responded with ${res.status}`);

      const data = await res.json();

      // Handle stringified body if necessary
      let finalData = Array.isArray(data) ? data : (data.body ? JSON.parse(data.body) : data);

      // Update state and sort by Code
      setAdminPrices(finalData.sort((a, b) => (a.Code || "").localeCompare(b.Code || "")));

    } catch (e) {
      console.error("Admin Fetch failed:", e.message);
    }
  }, [instance, accounts]);

  const handleAdminLogin = useCallback(async () => {
    try {
      await instance.loginPopup(loginRequest); // loginRequest to define the 'scope' (User.Read)
      fetchAdminPrices();
    } catch (e) {
      console.error("Login failed:", e);
    }
  }, [instance, fetchAdminPrices]);

  const addNewRow = useCallback(() => {
    const newRow = { Code: "NEW_" + Date.now(), Description: "New Component", Price: 0, isNew: true };
    setAdminPrices([...adminPrices, newRow]);
    setChangedRows(prev => new Set(prev).add(adminPrices.length));
  }, [adminPrices]);

  const markForDeletion = useCallback((index, code) => {
    if (window.confirm(`Are you sure you want to remove ${code}?`)) {
      // Add to deletion queue
      setDeletedCodes(prev => new Set(prev).add(code));

      // Remove from the local UI list
      const newPrices = [...adminPrices];
      newPrices.splice(index, 1);
      setAdminPrices(newPrices);

      // Clean up changedRows
      setChangedRows(prev => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
    }
  }, [adminPrices]);

  const handleFieldChange = useCallback((index, field, newValue) => {
    const newPrices = [...adminPrices];
    const item = newPrices[index];

    if (field === 'Price') {
      // Allow empty string or positive numbers (including 0 and decimals)
      if (newValue !== "" && !/^\d*\.?\d*$/.test(newValue)) return;
      item[field] = newValue;
    } else {
      item[field] = field === 'Code' ? newValue.toUpperCase() : newValue;
    }

    setAdminPrices(newPrices);
    setChangedRows(prev => new Set(prev).add(item.Code));
  }, [adminPrices]);

  const saveAdminPrices = useCallback(async () => {
    if (changedRows.size === 0 && deletedCodes.size === 0) return;

    setIsSaving(true);
    setLastSaved(null);

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });

      const token = tokenResponse.accessToken;
      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Process Deletions
      const deletionArray = Array.from(deletedCodes);
      for (const code of deletionArray) {
        const res = await fetch(LAMBDA_URL, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ action: "deletePrice", Code: code })
        });
        if (!res.ok) console.error(`Failed to delete ${code}`);
      }

      const updates = adminPrices.filter(item => changedRows.has(item.Code));
      for (const item of updates) {
        // Ensure price is a number, defaulting to 0
        const numericPrice = item.Price === "" ? 0 : parseFloat(item.Price);

        await fetch(LAMBDA_URL, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            action: "updatePrice",
            Code: item.Code,
            Price: numericPrice,
            Description: item.Description || ""
          })
        });
      }
      setLastSaved(new Date().toLocaleTimeString());
      setDeletedCodes(new Set());
      setChangedRows(new Set());
      // Ensure UI matches DB
      setTimeout(() => {
        fetchAdminPrices();
      }, 500);
    } catch (e) {
      console.error("Save failed", e);
      alert("Error saving changes. Check console.");
    } finally {
      setIsSaving(false);
    }
  }, [changedRows, deletedCodes, adminPrices, fetchAdminPrices, accounts, instance]);

  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const activeClones = useRef({});
  const controlsRef = useRef(null);

  // Smooth Transition
  const cameraTargetPos = useRef(new THREE.Vector3(6, 0.5, 1.9));
  const controlsTargetLookAt = useRef(new THREE.Vector3(0, 0, 0));
  const isAutoMoving = useRef(false);

  // Smooth Viewport Offset
  const currentFilmOffset = useRef(0);
  const targetFilmOffset = useRef(0);

  const [isCopied, setIsCopied] = useState(false);
  const [config, setConfig] = useState(initialState);
  const [loadStatus, setLoadStatus] = useState('Initializing...');
  const [scene, setScene] = useState(null);
  const [camera, setCamera] = useState(null);
  const [renderer, setRenderer] = useState(null);
  const [deviceParts, setDeviceParts] = useState({});
  const [modelLibrary, setModelLibrary] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [hoveredInnerVariant, setHoveredInnerVariant] = useState(null);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const indicatorContainerRef = useRef(null);
  const [prices, setPrices] = useState({ total: 0, discountedTotal: 0 });
  const [loadProgress, setLoadProgress] = useState(0);
  const [extraCost, setExtraCost] = useState('');
  const [printImage, setPrintImage] = useState(null);
  const [printPoints, setPrintPoints] = useState({});
  const fadeRegistry = useRef([]);
  const [wiring, setWiring] = useState([]);
  const [wiringImage, setWiringImage] = useState(null);

  // Indicator
  const dotRefs = useRef({});
  const indicatorsRef = useRef([]);
  const updateLinesRef = useRef(null);
  const lerpTrackers = useRef({}); // Stores individual 3D vectors for each indicator

  // Trigger the three engine component
  useThreeEngine({
    canvasRef, animationFrameRef, controlsRef, cameraTargetPos,
    controlsTargetLookAt, isAutoMoving, currentFilmOffset, targetFilmOffset,
    fadeRegistry, updateLinesRef, setLoadProgress, setLoadStatus,
    setDeviceParts, setModelLibrary, setScene, setCamera, setRenderer
  });

  // Trigger the mesh component
  useMeshUpdater({
    config, scene, modelLibrary, selectedSlot, deviceParts, activeClones, fadeRegistry, buttonColors
  });

  const indicators = useIndicators({
    selectedSlot, isOptionsOpen, hoveredInnerVariant, config, hoveredSlot,
    indicatorsRef, dotRefs, lerpTrackers, updateLinesRef,
    indicatorContainerRef, camera, renderer
  });

  // Validate positive floats
  const handleExtraCostInput = useCallback((val) => {
    // Regex allows empty string, numbers, and a single decimal point
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setExtraCost(val);
    }
  }, []);

  // Add additional charges
  const extraCostValue = parseFloat(extraCost) || 0;

  const configCode = useMemo(() => {
    const relay = config.inner_components.some(i => i.variant === 'relay') ? '16' : '14';
    const can = config.inner_components.some(i =>
      i.variant === 'can_single' || i.variant === 'can_double') ? 'C' : '0';
    const antenna = config.inner_components.some(i => i.variant === 'antenna') ? 'X' : '0';
    const holes = config.inner_components.some(i => i.variant === 'back_holes') ? 'B3B3' : '0000';
    const prefix = `S${relay}${can}${antenna}YA20`;
    const mid = `${config.Q5.code}${config.Q6.code}${config.Q7.code}${config.Q8.code}${config.Q9.code}`;
    const suffix = `${config.Q1.code}${config.Q2.code}${config.Q3.code}${config.Q4.code}`;
    return `${prefix}-${mid}-${suffix}-${holes}-AA`.toUpperCase();
  }, [config]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(configCode);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }, [configCode]);

  const handleCodeInput = useCallback((val) => {
    const clean = val.toUpperCase().replace(/\s/g, '');
    if (clean.length < 30) return;
    try {
      const parts = clean.split('-');
      const relayCode = parts[0].substring(1, 3);
      const canCode = parts[0].substring(3, 4);
      const antennaCode = parts[0].substring(4, 5);
      const q5Code = parts[1].substring(0, 2);
      const q6Code = parts[1].substring(2, 4);
      const q7Code = parts[1].substring(4, 6);
      const q8Code = parts[1].substring(6, 8);
      const q9Code = parts[1].substring(8, 10);
      const q1Code = parts[2].substring(0, 2);
      const q2Code = parts[2].substring(2, 4);
      const q3Code = parts[2].substring(4, 6);
      const q4Code = parts[2].substring(6, 8);
      const holesCode = parts[3];

      const newInner = [];
      if (relayCode === '16') newInner.push({ variant: 'relay', name: 'Relay' });
      if (canCode === 'C') newInner.push({ variant: 'can_single', name: 'Single CAN' });
      if (antennaCode === 'X') newInner.push({ variant: 'antenna', name: 'Antenna' });
      if (holesCode === 'B3B3') newInner.push({ variant: 'back_holes', name: 'Back Holes' });

      setConfig({
        Q1: connectorOptions.find(o => o.code === q1Code) || connectorOptions[0],
        Q2: connectorOptions.find(o => o.code === q2Code) || connectorOptions[0],
        Q3: connectorOptions.find(o => o.code === q3Code) || connectorOptions[0],
        Q4: connectorOptions.find(o => o.code === q4Code) || connectorOptions[0],
        Q5: housingOptionsFull.find(o => o.code === q5Code) || housingOptionsFull[0],
        Q6: housingOptionsRestricted.find(o => o.code === q6Code) || housingOptionsRestricted[0],
        Q7: housingOptionsRestricted.find(o => o.code === q7Code) || housingOptionsRestricted[0],
        Q8: housingOptionsRestricted.find(o => o.code === q8Code) || housingOptionsRestricted[0],
        Q9: q9Code === 'LD' ? { variant: 'top', name: 'LED Display', code: 'LD' } : {
          variant: 'basic_top', name: 'No LED Display', code: '00'
        },
        inner_components: newInner
      });
    } catch (e) { console.error("Invalid Code"); }
  }, []);

  const handleReset = useCallback(() => {
    if (window.confirm("Reset configuration to default?")) {
      setConfig(initialState);
      setIsOptionsOpen(false);
      setSelectedSlot(null);
      isAutoMoving.current = true;
      cameraTargetPos.current.set(6, 0.5, 1.9);
      controlsTargetLookAt.current.set(0, 0, 0);
      targetFilmOffset.current = (415 / window.innerWidth) * 18;
    }
  }, []);

  const toggleInnerComponent = useCallback((option) => {
    setConfig(prev => {
      let currentSelection = [...prev.inner_components];
      const isSelected = currentSelection.find(item => item.variant === option.variant);
      if (isSelected) {
        currentSelection = currentSelection.filter(item => item.variant !== option.variant);
      } else {
        if (option.variant === 'can_single') currentSelection = currentSelection.filter(item =>
          item.variant !== 'can_double'
        );
        else if (option.variant === 'can_double') currentSelection = currentSelection.filter(item =>
          item.variant !== 'can_single'
        );
        currentSelection.push(option);
      }
      return { ...prev, inner_components: currentSelection };
    });
  }, []);

  const handlePrint = useCallback(() => {
    if (!renderer || !camera || !scene || !controlsRef.current) return;

    const oldPos = camera.position.clone();
    const oldTarget = controlsRef.current.target.clone();
    const oldAspect = camera.aspect;
    const oldSize = new THREE.Vector2();
    renderer.getSize(oldSize);

    // Fixed capture resolution
    const captureWidth = 1920;
    const captureHeight = 1080;
    renderer.setSize(captureWidth, captureHeight, false);
    camera.aspect = captureWidth / captureHeight;
    camera.updateProjectionMatrix();

    // Position camera
    camera.position.set(6, 0.5, 1.9);
    controlsRef.current.target.set(0, 0, 0);
    controlsRef.current.update();

    renderer.render(scene, camera);
    const dataUrl = canvasRef.current.toDataURL('image/png');

    const pPoints = {};
    const tempVec = new THREE.Vector3();

    Object.keys(slotPositions).forEach((id) => {
      tempVec.set(slotPositions[id].x, slotPositions[id].y, slotPositions[id].z);
      const v = tempVec.project(camera);
      const idNum = parseInt(id.replace('Q', ''));

      let sideOffset = (idNum % 2 === 0) ? 24 : -24;
      let verticalOffset = 2;

      if (id === 'Q1') { sideOffset = -16; verticalOffset = 8; }
      if (id === 'Q2') { sideOffset = -18; verticalOffset = -2; }
      if (id === 'Q3') { sideOffset = 19; verticalOffset = 7; }
      if (id === 'Q4') { verticalOffset = -1; }

      pPoints[id] = {
        left: (v.x * 0.5 + 0.5) * 100 + sideOffset + '%',
        top: (-v.y * 0.5 + 0.5) * 100 + verticalOffset + '%'
      };
    });

    setPrintPoints(pPoints);
    setPrintImage(dataUrl);

    // Restore camera
    renderer.setSize(oldSize.x, oldSize.y, true);
    camera.aspect = oldAspect;
    camera.updateProjectionMatrix();
    camera.position.copy(oldPos);
    controlsRef.current.target.copy(oldTarget);
    controlsRef.current.update();

    setTimeout(() => { window.print(); }, 800);
  }, [renderer, camera, scene]);

  const [isPriceLoading, setIsPriceLoading] = useState(false);

  const fetchPrices = useCallback(async (signal) => {
    if (!isAuthenticated) return;
    setIsPriceLoading(true);
    try {
      // Get Token
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      const token = tokenResponse.accessToken;

      const response = await fetch(LAMBDA_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ configCode: configCode }),
        signal: signal
      });

      const data = await response.json();
      let result = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;

      setPrices({
        total: parseFloat(result.total ?? result.Total ?? 0),
        discountedTotal: parseFloat(result.discountedTotal ?? result.DiscountedTotal ?? 0)
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error("Price fetch failed", error);
    } finally {
      if (!signal?.aborted) setIsPriceLoading(false);
    }
  }, [configCode, isAuthenticated, instance, accounts]);

  useEffect(() => {
    const controller = new AbortController();
    if (configCode.length > 20) {
      fetchPrices(controller.signal);
    }
    return () => controller.abort();
  }, [configCode, fetchPrices]);

  useEffect(() => {
    // Dynamic offset based on Sidebar (415px) + Submenu (320px)
    const sidebarWidth = isOptionsOpen ? 735 : 415;
    targetFilmOffset.current = (sidebarWidth / window.innerWidth) * 18;
  }, [isOptionsOpen]);

  // Enforce Q6-Q8 Empty when Q5 is Empty
  useEffect(() => {
    if (config.Q5.variant === 'none') {
      const emptyOption = housingOptionsRestricted.find(o => o.variant === 'none') || { variant: 'none', name: 'Empty', code: '00' };

      // Only update if they aren't already empty (prevents infinite loops)
      if (config.Q6.variant !== 'none' || config.Q7.variant !== 'none' || config.Q8.variant !== 'none') {
        setConfig(prev => ({
          ...prev,
          Q6: emptyOption,
          Q7: emptyOption,
          Q8: emptyOption
        }));
      }
    }
  }, [config.Q5, config.Q6, config.Q7, config.Q8]);

  return (
    <div style={{ width: '100vw', height: '100vh', background: theme.bg, fontFamily: "'Roboto', sans-serif", overflow: 'hidden', position: 'relative', boxSizing: 'border-box' }}>
      {loadStatus !== 'Ready!' && (
        <div style={{ position: 'absolute', inset: 0, background: '#ffffff', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontWeight: 900, fontSize: '24px', color: theme.primary, letterSpacing: '2px', marginBottom: '20px' }}>{loadStatus}</div>
          <div style={{ width: '300px', height: '6px', background: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ width: `${loadProgress}%`, height: '100%', background: theme.primary, transition: 'width 0.3s ease-out', boxShadow: `0 0 10px ${theme.primary}88` }} />
          </div>
          <div style={{ marginTop: '15px', fontSize: '10px', fontWeight: 700, color: theme.textMuted }}>INITIALIZING 3D ENGINE</div>
        </div>
      )}
      {/* LOGIN ICON */}
      {!isAuthenticated ? (
        // Login button (when not logged in)
        <div onClick={handleAdminLogin} style={{ position: 'absolute', top: '42%', left: '30px', transform: 'translateY(50%)', zIndex: 150, background: 'rgb(255, 215, 0)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: `1px solid ${theme.border}`, pointerEvents: 'auto' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(0, 40, 70)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
        </div>
      ) : isAdmin ? (
        // Price list button (only for admins when logged in)
        <div onClick={() => { fetchAdminPrices(); setIsModalOpen(true); }} style={{ position: 'absolute', top: '42%', left: '30px', transform: 'translateY(50%)', zIndex: 150, background: 'rgb(255, 215, 0)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: `1px solid ${theme.border}`, pointerEvents: 'auto' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(0, 40, 70)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
        </div>
      ) : null}
      {/* I/O ICON */}
      <div onClick={() => setIsIODesignerOpen(true)} style={{ position: 'absolute', top: '47%', left: '30px', transform: 'translateY(60%)', zIndex: 150, background: 'rgb(255, 215, 0)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: `1px solid ${theme.border}`, pointerEvents: 'auto' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(0, 40, 70)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
          <rect x="9" y="9" width="6" height="6"></rect>
          <line x1="9" y1="1" x2="9" y2="4"></line>
          <line x1="15" y1="1" x2="15" y2="4"></line>
          <line x1="9" y1="20" x2="9" y2="23"></line>
          <line x1="15" y1="20" x2="15" y2="23"></line>
          <line x1="20" y1="9" x2="23" y2="9"></line>
          <line x1="20" y1="14" x2="23" y2="14"></line>
          <line x1="1" y1="9" x2="4" y2="9"></line>
          <line x1="1" y1="14" x2="4" y2="14"></line>
        </svg>
      </div>
      {/* PRINT ICON */}
      <div onClick={handlePrint} style={{ position: 'absolute', top: '52.5%', left: '30px', transform: 'translateY(60%)', zIndex: 150, background: 'rgb(255, 215, 0)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: `1px solid ${theme.border}`, pointerEvents: 'auto' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(0, 40, 70)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
      </div>
      {/* HEADER */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '70px', padding: '0 30px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 110, background: 'rgb(255, 215, 0)', userSelect: 'none' }}>
        <div style={{ fontWeight: 900, fontSize: '20px', letterSpacing: '0.5px', color: '#1e293b' }}>SAFETY SIMPLIFIER <span style={{ color: 'rgb(0, 40, 70)' }}>CONFIGURATOR</span></div>
        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" style={{ height: '45px' }} />
      </div>
      {/* DOT INDICATOR */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
        <svg ref={indicatorContainerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10, overflow: 'visible' }}>
          {indicators.map((ind, index) => (
            <g
              key={index}
              ref={el => {
                if (el) {
                  // Cache the parent group and the child circles in one object
                  dotRefs.current[index] = {
                    group: el,
                    ping: el.querySelector('.ping-circle'),
                    main: el.querySelector('.main-dot')
                  };
                }
              }}
              className="indicator-dot"
            >
              <circle className="ping-circle" r="18" fill={ind.color} style={{ animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
              <circle className="main-dot" r="9" fill={ind.color} />
            </g>
          ))}
        </svg>
      </div>

      <div style={{ position: 'absolute', right: 0, top: 0, height: '100%', display: 'flex', pointerEvents: 'none', zIndex: 101, userSelect: 'none' }}>
        {/* Sub-Sidebar */}
        <OptionsSidebar
          isOpen={isOptionsOpen}
          selectedSlot={selectedSlot}
          config={config}
          componentOptions={componentOptions}
          setHoveredInnerVariant={setHoveredInnerVariant}
          toggleInnerComponent={toggleInnerComponent}
          setConfig={setConfig}
          setIsOptionsOpen={setIsOptionsOpen}
          setSelectedSlot={setSelectedSlot}
          cameraTargetPos={cameraTargetPos}
          controlsTargetLookAt={controlsTargetLookAt}
          isAutoMoving={isAutoMoving}
          buttonColors={buttonColors}
          setButtonColors={setButtonColors}
        />
        {/* Main Sidebar */}
        <Sidebar
          config={config}
          selectedSlot={selectedSlot}
          setSelectedSlot={setSelectedSlot}
          setIsOptionsOpen={setIsOptionsOpen}
          setHoveredSlot={setHoveredSlot}
          componentOptions={componentOptions}
          isAuthenticated={isAuthenticated}
          extraCost={extraCost}
          handleExtraCostInput={handleExtraCostInput}
          isPriceLoading={isPriceLoading}
          prices={prices}
          extraCostValue={extraCostValue}
          configCode={configCode}
          handleCodeInput={handleCodeInput}
          copyToClipboard={copyToClipboard}
          isCopied={isCopied}
          handleReset={handleReset}
          setIsContactModalOpen={setIsContactModalOpen}
          cameraTargetPos={cameraTargetPos}
          controlsTargetLookAt={controlsTargetLookAt}
          isAutoMoving={isAutoMoving}
          onOpenWarning={() => setIsWarningOpen(true)}
        />
      </div>

      <WarningModal
        isOpen={isWarningOpen}
        onClose={() => setIsWarningOpen(false)}
      />
      <IODesigner
        config={config}
        isOpen={isIODesignerOpen}
        edges={wiring}
        setEdges={setWiring}
        nodes={ioNodes}
        setNodes={setIoNodes}
        onClose={() => setIsIODesignerOpen(false)}
        onSaveImage={setWiringImage}
      />
      <PrintableArea
        printImage={printImage}
        printPoints={printPoints}
        config={config}
        configCode={configCode}
        prices={prices}
        extraCostValue={extraCostValue}
        wiring={wiring}
        wiringImage={wiringImage}
      />
      <AdminModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        adminPrices={adminPrices}
        addNewRow={addNewRow}
        handleFieldChange={handleFieldChange}
        markForDeletion={markForDeletion}
        saveAdminPrices={saveAdminPrices}
        fetchAdminPrices={fetchAdminPrices}
        fetchPrices={fetchPrices}
        isSaving={isSaving}
        lastSaved={lastSaved}
        changedRows={changedRows}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        configCode={configCode}
        isCopied={isCopied}
        copyToClipboard={copyToClipboard}
      />
    </div>
  );
};

export default Configurator;